import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { Box, Typography, useTheme } from "@mui/material";
import { ThreeDots } from "react-loader-spinner";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

// Components
import BloodPChart from "./Main/BloodPChart.js";
import WeightChart from "./Main/WeightChart.js";
import Ecgs from "./Main/Ecgs.js";
import Activities from "./Main/Activities.js";
import CardioR from "./Main/CardioR.js";
import Hafnayot from "./Main/Hafnayot.js";
import Patients from "./Main/Patients.js";
import Invoices from "./Main/Invoices.js";

import resultsService from "../../Services/resultsService.js";

const MySwal = withReactContent(Swal);
const jcc = require("json-case-convertor");

function Main(props) {
  const navigate = useNavigate();
  const theme = useTheme(); // גישה ל-Theme לצורך צבעים דינמיים (כמו ב-Loader)
  const userDetails = jcc.snakeCaseKeys(props.userDetails);
  const { page } = useParams();

  // בדיקת התחברות
  useEffect(() => {
    if (!props.userDetails) {
      navigate("/login");
    }
  }, [props.userDetails, navigate]);

  // בדיקת אישור דיגיטלי (Swal)
  useEffect(() => {
    if (props.userDetails) {
      resultsService.checkDigital().then((p) => {
        if (!p?.data) {
          swalFuncAgree();
        }
      });
    }
  }, []);

  // גלילה למעלה במעבר דף
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  // ניתוב אוטומטי בדף הבית (undefined)
  useEffect(() => {
    if (page === undefined) {
      if (
        userDetails?.ipermit &&
        userDetails?.ipermit !== 1 &&
        props.patients
      ) {
        navigate("/patients");
      }
      if (userDetails?.ipermit === 1 && props.cardioRResults) {
        if (props.cardioRResults.length > 0) {
          navigate("/cardioR");
        } else {
          navigate("/activities");
        }
      }
    }
  }, [props, page, userDetails?.ipermit, navigate]);

  const swalFuncAgree = async () => {
    await MySwal.fire({
      confirmButtonColor: theme.palette.primary.main,
      confirmButtonText: "אישור",
      cancelButtonText: "ביטול",
      showCancelButton: true,
      html: (
        <div style={{ textAlign: "right", direction: "rtl" }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            • למען שמירה על איכות הסביבה ושיפור השירות הניתן ללקוחותינו, שחל
            עוברת לשליחת מסרי שירות באמצעים דיגיטליים בלבד.
            <br />• כחלק משירות זה תשלחנה חשבוניות, פירוט תשלומים שנתי, קבלות
            ומסרי שירות אל הטלפון הנייד שברשותך או הדואר האלקטרוני שלך. <br /> •
            כחלק משיפור השירות שחל מציעה מעת לעת הטבות, וובינרים, מוצרים ועוד
            ללקוחותיה.
          </Typography>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              marginBottom: "10px",
            }}
          >
            <input
              type="checkbox"
              id="checkBoxAgree1"
              style={{ marginTop: "5px" }}
            />
            <label htmlFor="checkBoxAgree1" style={{ marginRight: "8px" }}>
              הבנתי ואני מסכימ/ה ומאשרת לקבלת מסרי שירות וחשבוניות, פירוט
              תשלומים שנתי, קבלות באמצעים דיגיטליים.
            </label>
          </div>
          <div style={{ display: "flex", alignItems: "flex-start" }}>
            <input
              type="checkbox"
              id="checkBoxAgree2"
              style={{ marginTop: "5px" }}
            />
            <label htmlFor="checkBoxAgree2" style={{ marginRight: "8px" }}>
              הבנתי ואני מסכימ/ה ומאשרת לקבלת הטבות, מסרי שיווק הזמנות לוובינר
              ומידע על מוצרים ושירותים חדשים באמצעים דיגיטליים.
            </label>
          </div>
          <Typography variant="caption" sx={{ display: "block", mt: 2 }}>
            • באפשרותך בכל עת לשנות או לעדכן את פרטי ההתקשרות הדיגיטליים עמך
            בפנייה לשירות הלקוחות
          </Typography>
        </div>
      ),
    }).then((a) => {
      const checkbox1 = document.getElementById("checkBoxAgree1").checked;
      const checkbox2 = document.getElementById("checkBoxAgree2").checked;
      if (a.isConfirmed && checkbox1) {
        let digitalEntity = {
          custpointer: 0,
          agreeDigitalInformation: checkbox1,
          agreeDigitalMarketing: checkbox2,
        };
        resultsService.insertDigital(digitalEntity);
      } else {
        alert("כדי לצפות באיזור האישי נא לאשר קבלת מידע דיגיטלי");
        swalFuncAgree();
      }
    });
  };

  const showPage = () => {
    switch (page) {
      case "weight":
        document.title = "איזור אישי: מדידות משקל";
        return <WeightChart />;
      case "patients":
        document.title = "איזור אישי: רשימת לקוחות";
        return <Patients />;
      case "invoices":
        document.title = "איזור אישי: חשבוניות";
        return <Invoices />;
      case "hafnayot":
        document.title = "איזור אישי: הפניות";
        return <Hafnayot />;
      case "bloodP":
        document.title = "איזור אישי: מדידות לחץ דם";
        return <BloodPChart />;
      case "ecg":
        document.title = "איזור אישי: מדידות אק.ג";
        return <Ecgs />;
      case "activities":
        document.title = "איזור אישי: פעילויות אחרונות";
        return <Activities />;
      case "cardioR":
        document.title = "איזור אישי: קרדיו אר";
        return <CardioR />;
      case undefined:
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "60vh",
              textAlign: "center",
            }}
          >
            <Typography variant="h5" color="text.primary" sx={{ mb: 2 }}>
              אנחנו מכינים עבורך את התיק האישי...
            </Typography>
            <ThreeDots
              height="80"
              width="80"
              radius="9"
              color={theme.palette.primary.main} // הכתום מה-Theme
              visible={true}
            />
          </Box>
        );
      default:
        return (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <Typography variant="h4" color="text.primary">
              לצערנו הדף שחיפשת אינו קיים
            </Typography>
          </Box>
        );
    }
  };

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        bgcolor: "background.default", // שימוש ברקע הדינמי מה-Theme
        minHeight: "100vh",
        p: { xs: 2, md: 3 },
        transition: "background-color 0.3s ease, color 0.3s ease", // אנימציה חלקה במעבר
      }}
    >
      {showPage()}
    </Box>
  );
}

const mapStateToProps = (state) => {
  return {
    userDetails: state.userDetails,
    cardioRResults: state.cardioRResults,
    patients: state.patients,
    darkMode: state.darkMode,
  };
};

export default connect(mapStateToProps)(Main);
